import React from 'react';
import { Flex, Form, Select } from '@app/ui';
import { useTranslation } from 'react-i18next';
import delimiters from '@app/constants/delimiters';
import { dateFormatOptions } from '@app/constants/dateFormat';
import { FormikProps } from 'formik';
import { FormValues } from '@app/settings/hooks/useRegionForm';
import { SUPPORTED_LANGUAGES, LANGUAGE_NAMES } from '@app/i18n/constants';

type RegionFormProps = {
    formInstance: FormikProps<FormValues>;
};

export const RegionForm = ({ formInstance }: RegionFormProps) => {
    const { t } = useTranslation('settings');
    const { touched, errors, values, setFieldValue, handleBlur } = formInstance;
    const languageError = !!(touched.language && errors.language);
    const placementError = !!(touched.placement && errors.placement);
    const dateError = !!(touched.date && errors.date);

    return (
        <Flex vertical className="-form">
            <form>
                <Form.Item
                    layout="vertical"
                    className="-m-b-3"
                    label={t('userSettings.region.form.delimiter')}
                    validateStatus={placementError ? 'error' : undefined}
                    help={
                        placementError && typeof errors.placement === 'string'
                            ? errors.placement
                            : undefined
                    }
                >
                    <Select
                        value={values.placement}
                        onChange={value => setFieldValue('placement', value)}
                        onBlur={() =>
                            handleBlur({ target: { name: 'placement' } })
                        }
                        options={[
                            {
                                value: null,
                                label: t(
                                    'userSettings.region.form.placeholders.selectCsvDelimiter',
                                ),
                            },
                            ...delimiters.map(delimiter => ({
                                value: delimiter.value,
                                label: t(delimiter.i18nKey),
                            })),
                        ]}
                        placeholder={t(
                            'userSettings.region.form.placeholders.selectDelimiter',
                        )}
                    />
                </Form.Item>

                <Form.Item
                    layout="vertical"
                    className="-m-b-3"
                    label={t('userSettings.region.form.dateFormat')}
                    validateStatus={dateError ? 'error' : undefined}
                    help={
                        dateError && typeof errors.date === 'string'
                            ? errors.date
                            : undefined
                    }
                >
                    <Select
                        value={values.date}
                        onChange={value => setFieldValue('date', value)}
                        onBlur={() => handleBlur({ target: { name: 'date' } })}
                        options={[
                            {
                                value: null,
                                label: t(
                                    'userSettings.region.form.placeholders.selectDateFormatOption',
                                ),
                            },
                            ...dateFormatOptions.map(option => ({
                                value: option.value,
                                label: option.name,
                            })),
                        ]}
                        placeholder={t(
                            'userSettings.region.form.placeholders.selectDateFormat',
                        )}
                    />
                </Form.Item>
                <Form.Item
                    layout="vertical"
                    label={t('userSettings.region.form.language')}
                    validateStatus={languageError ? 'error' : undefined}
                    help={
                        languageError && typeof errors.language === 'string'
                            ? errors.language
                            : undefined
                    }
                >
                    <Select
                        value={values.language}
                        onChange={value => setFieldValue('language', value)}
                        onBlur={() =>
                            handleBlur({ target: { name: 'language' } })
                        }
                        options={SUPPORTED_LANGUAGES.map(langCode => ({
                            value: langCode,
                            label: LANGUAGE_NAMES[langCode],
                        }))}
                        placeholder={t(
                            'userSettings.region.form.placeholders.selectLanguage',
                        )}
                    />
                </Form.Item>
            </form>
        </Flex>
    );
};

export default RegionForm;
